/**
 * Logique PURE de l'éditeur de réglages resto (étape 2 — sous-étape 3A admin).
 *
 * Édition des horaires (`opening_hours`), des fermetures, et des champs identité
 * (ville, gérant, réseaux, notes). Aucune I/O ici : tout est testable. La
 * sauvegarde (RPC / update Supabase) est dans tenantSettingsService.ts.
 *
 * Rappels parité :
 *  - 7 jours, ordre Lundi→Dimanche (M14) ;
 *  - créneaux triés par heure de début à la sauvegarde (M14) ;
 *  - fermeture après minuit autorisée (fin < début OK) ;
 *  - réseaux vides nettoyés (M12) ; notes ≤ 0 = pas de badge.
 */

import type { OpeningDay, TenantSocials } from './database.types';
import type { SocialKey } from './socials';
import { SOCIAL_NETWORKS } from './socials';

/** Ordre humain des jours (lundi → dimanche). */
export const DAYS_FR = [
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
  'Dimanche',
] as const;

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

/** Valide un horaire "HH:MM" (00:00 → 23:59). */
export function isValidTime(s: string): boolean {
  return typeof s === 'string' && TIME_RE.test(s.trim());
}

function sortSlots(slots: [string, string][]): [string, string][] {
  return slots
    .filter((s) => Array.isArray(s) && s.length === 2)
    .slice()
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));
}

/**
 * Garantit exactement 7 jours dans l'ordre Lundi→Dimanche, slots triés.
 * Les jours absents sont fermés ; un jour `open:false` garde slots = [].
 */
export function normalizeOpeningHours(input: OpeningDay[] | null | undefined): OpeningDay[] {
  const byName = new Map<string, OpeningDay>();
  (Array.isArray(input) ? input : []).forEach((d) => {
    if (d && typeof d.day === 'string') byName.set(d.day.trim().toLowerCase(), d);
  });
  return DAYS_FR.map((name) => {
    const found = byName.get(name.toLowerCase());
    const open = found ? found.open !== false : false;
    const slots = open && Array.isArray(found?.slots) ? sortSlots(found!.slots) : [];
    return { day: name, open, slots };
  });
}

function mapDay(
  days: OpeningDay[],
  dayName: string,
  fn: (d: OpeningDay) => OpeningDay,
): OpeningDay[] {
  return days.map((d) => (d.day.toLowerCase() === dayName.toLowerCase() ? fn(d) : d));
}

/** Ouvre/ferme un jour. Fermer vide ses slots ; ouvrir un jour vide ajoute un service par défaut. */
export function setDayOpen(days: OpeningDay[], dayName: string, open: boolean): OpeningDay[] {
  return mapDay(days, dayName, (d) => ({
    ...d,
    open,
    slots: open ? (d.slots.length ? d.slots : [['11:30', '14:00']]) : [],
  }));
}

/** Ajoute un service à un jour (ouvert d'office). */
export function addSlot(
  days: OpeningDay[],
  dayName: string,
  slot: [string, string] = ['18:00', '22:00'],
): OpeningDay[] {
  return mapDay(days, dayName, (d) => ({ ...d, open: true, slots: [...d.slots, slot] }));
}

/** Modifie l'heure d'ouverture (which=0) ou de fermeture (which=1) d'un service. */
export function updateSlot(
  days: OpeningDay[],
  dayName: string,
  index: number,
  which: 0 | 1,
  value: string,
): OpeningDay[] {
  return mapDay(days, dayName, (d) => ({
    ...d,
    slots: d.slots.map((s, i) => {
      if (i !== index) return s;
      const next: [string, string] = [s[0], s[1]];
      next[which] = value;
      return next;
    }),
  }));
}

/** Supprime un service. Si le jour n'a plus de service, il devient fermé. */
export function removeSlot(days: OpeningDay[], dayName: string, index: number): OpeningDay[] {
  return mapDay(days, dayName, (d) => {
    const slots = d.slots.filter((_, i) => i !== index);
    return { ...d, slots, open: slots.length > 0 };
  });
}

/**
 * Prépare les horaires pour la sauvegarde : 7 jours ordonnés + slots triés (M14).
 * À appeler juste avant d'envoyer à la RPC.
 */
export function sortForSave(days: OpeningDay[]): OpeningDay[] {
  return normalizeOpeningHours(days);
}

/** Erreurs de saisie (format d'horaire). Vide = OK. */
export function validateOpeningHours(days: OpeningDay[]): string[] {
  const errors: string[] = [];
  days.forEach((d) => {
    if (!d.open) return;
    d.slots.forEach(([o, c], i) => {
      if (!isValidTime(o) || !isValidTime(c)) {
        errors.push(`${d.day} — service ${i + 1} : horaire invalide (format HH:MM attendu).`);
      }
    });
  });
  return errors;
}

/** Nettoie les réseaux : trim + suppression des clés vides (M12). */
export function cleanSocials(socials: Partial<Record<SocialKey, string>> | null | undefined): TenantSocials {
  const out: TenantSocials = {};
  if (!socials) return out;
  SOCIAL_NETWORKS.forEach((n) => {
    const v = socials[n.key];
    if (typeof v === 'string' && v.trim().length > 0) out[n.key] = v.trim();
  });
  return out;
}

/** Parse une note (0..5). Renvoie null si vide/invalide ou ≤ 0 (badge masqué). */
export function parseRating(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined || input === '') return null;
  const n = typeof input === 'number' ? input : parseFloat(String(input).replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(5, Math.round(n * 10) / 10);
}

/** Parse un nombre d'avis entier ≥ 0. */
export function parseCount(input: string | number | null | undefined): number {
  if (input === null || input === undefined || input === '') return 0;
  const n = typeof input === 'number' ? input : parseInt(String(input), 10);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}
